import React, { useState, useRef, useEffect } from 'react';
import { Bot, Send, Trash2, MessageSquare, Sparkles } from 'lucide-react';

interface Message {
  id: string;
  type: 'user' | 'ai';
  content: string;
  timestamp: string;
}

interface Conversation {
  id: string;
  title: string;
  messages: Message[];
  lastUpdated: string;
}

export function AIHelperView() {
  const [conversations, setConversations] = useState<Conversation[]>([
    {
      id: '1',
      title: 'Tax Delinquent Blueprint Strategy',
      lastUpdated: 'Dec 5, 2025',
      messages: [
        {
          id: '1',
          type: 'user',
          content: 'How do I find exclusive tax delinquent leads using the TDB methodology?',
          timestamp: '2:45 PM'
        },
        {
          id: '2',
          type: 'ai',
          content: 'The Tax Delinquent Blueprint methodology focuses on generating **exclusive inbound leads** from motivated sellers in must-sell positions. Here\'s the core strategy:\n\n**1. Identify Must-Sell Properties**\n- Target properties with 2+ years of tax delinquency\n- Focus on total delinquent amount >$5,000 for serious situations\n- Look for owner-occupied properties (more motivated)\n\n**2. Multi-Channel Outreach**\n- **Yellow Letters**: Handwritten-style letters showing empathy\n- **Direct Mail**: Professional offers with solutions\n- **Cold Calling**: Follow up 3-5 days after mail hits\n- **Door Knocking**: For high-value opportunities\n\n**3. Lead Nurturing**\n- Provide FREE property analysis\n- Offer multiple solutions (subject-to, seller financing, cash offer)\n- Build trust through education, not pressure\n\n**4. Exclusive Positioning**\n- These leads are OFF-MARKET (competitors don\'t know about them)\n- You\'re solving a real problem (tax burden)\n- Speed matters - many are facing foreclosure\n\nWant specific scripts for initial contact or negotiation strategies?',
          timestamp: '2:45 PM'
        }
      ]
    }
  ]);

  const [currentConversationId, setCurrentConversationId] = useState<string>('1');
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const currentConversation = conversations.find(c => c.id === currentConversationId);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [currentConversation?.messages]);

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || !currentConversation) return;

    // Add user message
    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: inputMessage,
      timestamp: new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    };

    const updatedConversations = conversations.map(conv => {
      if (conv.id === currentConversationId) {
        return {
          ...conv,
          messages: [...conv.messages, userMessage],
          lastUpdated: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
        };
      }
      return conv;
    });

    setConversations(updatedConversations);
    setInputMessage('');
    setIsTyping(true);

    // Simulate AI response
    setTimeout(() => {
      const aiMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'ai',
        content: `Great question about "${inputMessage}"!\n\nBased on the **Tax Delinquent Blueprint methodology**, here's what you need to know:\n\n🎯 This AI assistant will be powered by the complete TDB course knowledge base, including:\n- All 11 core lessons from Jason Palliser and Jason Lucchesi\n- Proven scripts and templates for multi-channel outreach\n- Due diligence checklists and property analysis frameworks\n- Creative financing strategies and deal structuring\n- Negotiation tactics for must-sell situations\n\n⚡ **Coming Soon**: Real-time AI responses with TDB expertise to help you:\n- Qualify leads faster\n- Structure better deals\n- Navigate complex tax situations\n- Scale your business systematically\n\nFor now, this is a demo. The full AI backend integration is in development.`,
        timestamp: new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
      };

      setConversations(conversations.map(conv => {
        if (conv.id === currentConversationId) {
          return {
            ...conv,
            messages: [...conv.messages, userMessage, aiMessage],
            lastUpdated: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
          };
        }
        return conv;
      }));

      setIsTyping(false);
    }, 1500);
  };

  const handleNewConversation = () => {
    const newConversation: Conversation = {
      id: Date.now().toString(),
      title: 'New Conversation',
      messages: [],
      lastUpdated: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    };

    setConversations([newConversation, ...conversations]);
    setCurrentConversationId(newConversation.id);
  };

  const handleDeleteConversation = (conversationId: string) => {
    const updatedConversations = conversations.filter(c => c.id !== conversationId);
    setConversations(updatedConversations);

    if (conversationId === currentConversationId && updatedConversations.length > 0) {
      setCurrentConversationId(updatedConversations[0].id);
    } else if (updatedConversations.length === 0) {
      handleNewConversation();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="h-full flex gap-6">
      {/* Sidebar - Recent Conversations */}
      <div className="w-80 bg-white rounded-2xl shadow-lg flex flex-col">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-tdb-primary">Conversations</h2>
            <button
              onClick={handleNewConversation}
              className="p-2 bg-tdb-accent text-white rounded-lg hover:bg-[#0ea5ef] transition-all"
              title="New Conversation"
            >
              <MessageSquare className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {conversations.map((conversation) => (
            <div
              key={conversation.id}
              onClick={() => setCurrentConversationId(conversation.id)}
              className={`p-4 rounded-xl cursor-pointer transition-all group ${
                currentConversationId === conversation.id
                  ? 'bg-tdb-accent/10 border-2 border-tdb-accent'
                  : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="font-semibold text-gray-800 truncate">
                    {conversation.title}
                  </div>
                  <div className="text-sm text-gray-500 mt-1">
                    {conversation.messages.length} messages
                  </div>
                  <div className="text-xs text-gray-400 mt-1">
                    {conversation.lastUpdated}
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteConversation(conversation.id);
                  }}
                  className="opacity-0 group-hover:opacity-100 p-1 text-red-500 hover:bg-red-50 rounded transition-all"
                  title="Delete conversation"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 bg-white rounded-2xl shadow-lg flex flex-col">
        {/* Chat Header */}
        <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-tdb-primary to-tdb-secondary">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-tdb-accent rounded-xl flex items-center justify-center">
              <Bot className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Tax Delinquent Blueprint AI</h1>
              <p className="text-sm text-gray-200">Expert guidance on the TDB methodology and tax delinquent investing</p>
            </div>
          </div>
        </div>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {!currentConversation?.messages.length && (
            <div className="flex flex-col items-center justify-center h-full text-center px-8">
              <div className="w-20 h-20 bg-tdb-accent/10 rounded-full flex items-center justify-center mb-6">
                <Sparkles className="w-10 h-10 text-tdb-accent" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-3">Tax Delinquent Blueprint AI Assistant</h3>
              <p className="text-gray-600 max-w-2xl mb-6">
                Get expert guidance on the TDB methodology created by Jason Palliser and Jason Lucchesi. Ask about exclusive lead generation, must-sell property strategies, negotiation tactics, and scaling your tax delinquent investing business.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-3xl w-full">
                <button
                  onClick={() => setInputMessage('How do I identify the best tax delinquent properties to target?')}
                  className="p-4 bg-white border-2 border-gray-200 rounded-xl hover:border-tdb-accent hover:shadow-lg transition-all text-left"
                >
                  <div className="font-semibold text-gray-800 mb-1">🎯 Finding Hot Leads</div>
                  <div className="text-sm text-gray-600">Learn how to identify must-sell properties</div>
                </button>

                <button
                  onClick={() => setInputMessage('What are the best scripts for first contact with tax delinquent owners?')}
                  className="p-4 bg-white border-2 border-gray-200 rounded-xl hover:border-tdb-accent hover:shadow-lg transition-all text-left"
                >
                  <div className="font-semibold text-gray-800 mb-1">📞 Contact Scripts</div>
                  <div className="text-sm text-gray-600">Get proven outreach templates</div>
                </button>

                <button
                  onClick={() => setInputMessage('How do I structure creative financing deals for tax delinquent properties?')}
                  className="p-4 bg-white border-2 border-gray-200 rounded-xl hover:border-tdb-accent hover:shadow-lg transition-all text-left"
                >
                  <div className="font-semibold text-gray-800 mb-1">💰 Deal Structuring</div>
                  <div className="text-sm text-gray-600">Master creative financing strategies</div>
                </button>

                <button
                  onClick={() => setInputMessage('What\'s the due diligence checklist for tax delinquent properties?')}
                  className="p-4 bg-white border-2 border-gray-200 rounded-xl hover:border-tdb-accent hover:shadow-lg transition-all text-left"
                >
                  <div className="font-semibold text-gray-800 mb-1">✅ Due Diligence</div>
                  <div className="text-sm text-gray-600">Analyze properties like a pro</div>
                </button>
              </div>
            </div>
          )}

          {currentConversation?.messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div className={`max-w-[70%] ${message.type === 'user' ? 'order-2' : 'order-1'}`}>
                <div
                  className={`rounded-2xl p-4 ${
                    message.type === 'user'
                      ? 'bg-tdb-accent text-white'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  <div className="whitespace-pre-wrap">{message.content}</div>
                  <div
                    className={`text-xs mt-2 ${
                      message.type === 'user' ? 'text-blue-100' : 'text-gray-500'
                    }`}
                  >
                    {message.timestamp}
                  </div>
                </div>
              </div>
            </div>
          ))}

          {isTyping && (
            <div className="flex justify-start">
              <div className="bg-gray-100 rounded-2xl p-4 max-w-[70%]">
                <div className="flex space-x-2">
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex space-x-4">
            <textarea
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask about TDB strategies, lead generation, deal structuring, scripts..."
              className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-tdb-accent focus:border-tdb-accent"
              rows={2}
            />
            <button
              onClick={handleSendMessage}
              disabled={!inputMessage.trim() || isTyping}
              className={`px-6 py-3 rounded-xl font-semibold transition-all flex items-center space-x-2 ${
                inputMessage.trim() && !isTyping
                  ? 'bg-tdb-accent text-white hover:bg-[#0ea5ef] shadow-lg'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              <Send className="w-5 h-5" />
              <span>Send</span>
            </button>
          </div>
          <div className="mt-2 text-xs text-gray-500">
            Press Enter to send, Shift+Enter for new line
          </div>
        </div>
      </div>
    </div>
  );
}
